import type { Charge } from "@/types";

/** Les montants prévisionnels sont mensuels : l'annuel se déduit toujours ×12. */
export const ANNUAL_MULTIPLIER = 12;

export function annualOf(monthly: number): number {
  return Math.round((monthly || 0) * ANNUAL_MULTIPLIER);
}

/** Montant prévisionnel mensuel d'une charge pour un cycle (0 si non défini). */
export function chargeMonthlyFor(charge: Charge | undefined | null, cycleId: string): number {
  if (!charge) return 0;
  return charge.amounts.find((a) => a.cycleId === cycleId)?.monthly ?? 0;
}

/** Vrai si la charge couvre ce cycle (elle y a sa catégorie de dépense, même avec un budget encore à 0). */
export function chargeCoversCycle(charge: Charge, cycleId: string): boolean {
  return cycleId in charge.cycleCategoryIds;
}

/** Vrai si la charge couvre au moins un des cycles fournis. */
export function chargeHasAnyCycle(charge: Charge, cycleIds: string[]): boolean {
  return cycleIds.some((id) => id in charge.cycleCategoryIds);
}

/** Somme des montants mensuels d'une charge sur les cycles fournis. */
export function chargeTotalMonthly(charge: Charge, cycleIds: string[]): number {
  return charge.amounts
    .filter((a) => cycleIds.includes(a.cycleId))
    .reduce((sum, a) => sum + a.monthly, 0);
}

export type BudgetStatus = "none" | "safe" | "close" | "over";

/**
 * Compare un montant réel saisi au montant prévisionnel mensuel de la charge :
 * - `over`  : dépasse le prévisionnel (blocage de l'enregistrement)
 * - `close` : s'en approche (≥ 80 %) — avertissement
 * - `safe`  : nettement en-dessous — marge confortable
 */
export function budgetStatus(amount: number, provisional: number): BudgetStatus {
  if (!provisional || provisional <= 0 || amount <= 0) return "none";
  if (amount > provisional) return "over";
  if (amount >= provisional * 0.8) return "close";
  return "safe";
}

export const BUDGET_SEAL: Record<Exclude<BudgetStatus, "none">, number> = {
  safe: 0.8,
  close: 0.8,
  over: 1,
};

const ROMAN = ["I", "II", "III", "IV", "V", "VI", "VII", "VIII", "IX", "X", "XI", "XII"];

/** Numérotation romaine des catégories de charges (I, II, III...). */
export function romanNumeral(index: number): string {
  return ROMAN[index] ?? String(index + 1);
}

/** Icônes proposées pour les charges et leurs catégories. */
export const CHARGE_ICONS: string[] = [
  "ri-price-tag-3-line",
  "ri-building-2-line",
  "ri-team-line",
  "ri-tools-line",
  "ri-archive-2-line",
  "ri-more-line",
  "ri-wallet-3-line",
  "ri-user-star-line",
  "ri-home-4-line",
  "ri-flashlight-line",
  "ri-water-flash-line",
  "ri-brush-line",
  "ri-gas-station-line",
  "ri-computer-line",
  "ri-layout-4-line",
  "ri-pencil-ruler-2-line",
  "ri-wifi-line",
  "ri-bus-2-line",
  "ri-book-2-line",
  "ri-file-list-3-line",
  "ri-restaurant-2-line",
  "ri-hand-heart-line",
  "ri-stack-line",
  "ri-truck-line",
  "ri-hammer-line",
  "ri-settings-3-line",
];